// Administrador Master → Panel (ADR-0047). Counts from the database, consumption from the ad server's
// billable ledger × CPM in force. A metric without a verifiable source says "No disponible".
import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { Callout, PageHeader, Panel, Section } from "@/ui/layout";
import { PageError, PageLoading, useTitle } from "../common/page";
import { usd } from "../commercial/labels";
import { useAdmin } from "./admin-api";

interface Dashboard {
  counts: Record<string, number | string>;
  consumption: {
    available: boolean;
    billableImpressions: number | null;
    valueMicros: number | null;
    /** A newer measurement still advancing (report 106). */
    run?: { status: "RUNNING" | "COMPLETED" } | null;
  };
  /** CA-O4: registered invoices only (administrative records); test and real always apart. */
  overdue: {
    available: boolean;
    real?: { overdueMicros: number; overdueCount: number };
    test?: { overdueMicros: number; overdueCount: number };
  };
  roles: string[];
}

const n = (v: number | string | undefined) => Number(v ?? 0);
/** "1 empresa espera" / "3 empresas esperan". */
const count = (v: number | string | undefined, one: string, many: string) =>
  `${String(n(v))} ${n(v) === 1 ? one : many}`;

export function Kpi({ label, value, hint, to }: { label: string; value: string; hint?: string; to?: string }) {
  const body = (
    <Panel className="grid h-full gap-1 p-4 transition-colors hover:border-line-strong">
      <span className="text-xs text-muted">{label}</span>
      <span className="text-2xl font-semibold tabular-nums tracking-tight">{value}</span>
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </Panel>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export function AdminDashboard() {
  useTitle("Administrador Master");
  const { data, isLoading, error } = useAdmin<Dashboard>(
    "/admin/dashboard",
    {},
    (d) => d.consumption.run?.status === "RUNNING"
  );
  if (isLoading) return <PageLoading />;
  if (error || !data) return <PageError error={error} />;
  const c = data.counts;
  const approved = n(c.creditApprovedMicros);
  const committed = n(c.creditCommittedMicros);
  const alerts = [
    n(c.pendingReview) > 0 && {
      text: count(c.pendingReview, "empresa espera validación", "empresas esperan validación"),
      to: "/admin/companies?status=PENDING_REVIEW",
    },
    n(c.duplicateTaxIds) > 0 && {
      text: count(
        c.duplicateTaxIds,
        "con identificación tributaria duplicada",
        "con identificación tributaria duplicada"
      ),
      to: "/admin/companies?status=PENDING_REVIEW",
    },
    n(c.purchaseOrdersPending) > 0 && {
      text: count(c.purchaseOrdersPending, "orden de compra por revisar", "órdenes de compra por revisar"),
      to: "/admin/purchase-orders",
    },
    n(c.authorizationsPending) > 0 && {
      text: count(c.authorizationsPending, "campaña espera autorización", "campañas esperan autorización"),
      to: "/admin/authorizations",
    },
    n(c.notificationsFailed) > 0 && {
      text: count(c.notificationsFailed, "notificación fallida", "notificaciones fallidas"),
      to: "/admin/settings",
    },
    n(c.notificationsWithoutRecipients) > 0 && {
      text: count(
        c.notificationsWithoutRecipients,
        "notificación sin destinatario configurado",
        "notificaciones sin destinatario configurado"
      ),
      to: "/admin/settings",
    },
  ].filter(Boolean) as Array<{ text: string; to: string }>;
  return (
    <div className="grid gap-8">
      <PageHeader
        title="Panel"
        description="Operación comercial de InteractAds. Datos de staging marcados como prueba."
      />
      {alerts.length > 0 ? (
        <Section title="Alertas administrativas">
          <ul className="grid gap-1.5">
            {alerts.map((a) => (
              <li key={a.text}>
                <Link to={a.to} className="flex items-center gap-2 text-sm text-fg hover:underline">
                  <AlertTriangle className="size-4 text-warning" /> {a.text}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section title="Empresas y usuarios">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Kpi label="Agencias" value={String(n(c.agencies))} to="/admin/companies?orgType=AGENCY" />
          <Kpi
            label="Clientes directos"
            value={String(n(c.directClients))}
            to="/admin/companies?orgType=DIRECT_CLIENT"
          />
          <Kpi
            label="Pendientes de validación"
            value={String(n(c.pendingReview))}
            to="/admin/companies?status=PENDING_REVIEW"
          />
          <Kpi
            label="Sin perfil empresarial"
            value={String(n(c.organizationsWithoutProfile))}
            hint="Espacios existentes"
            to="/admin/companies?status=NO_PROFILE"
          />
          <Kpi label="Activas" value={String(n(c.active))} to="/admin/companies?status=ACTIVE" />
          <Kpi label="Suspendidas" value={String(n(c.suspended))} to="/admin/companies?status=SUSPENDED" />
          <Kpi label="Usuarios" value={String(n(c.users))} />
          <Kpi label="Campañas" value={String(n(c.campaigns))} />
        </div>
      </Section>
      <Section title="Operación comercial">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Kpi label="Órdenes de compra" value={String(n(c.purchaseOrders))} to="/admin/purchase-orders" />
          <Kpi label="Solicitudes pendientes" value={String(n(c.authorizationsPending))} to="/admin/authorizations" />
          <Kpi
            label="Impresiones facturables"
            value={
              data.consumption.available && data.consumption.billableImpressions !== null
                ? data.consumption.billableImpressions.toLocaleString("es-HN")
                : "No disponible"
            }
            hint="Ledger del Ad Server, últimos 400 días"
          />
          <Kpi
            label="Consumo CPM acumulado"
            value={data.consumption.available ? usd(data.consumption.valueMicros) : "No disponible"}
            hint="Impresiones × CPM vigente"
          />
          <Kpi label="Crédito aprobado" value={usd(approved)} to="/admin/finance" />
          <Kpi label="Crédito comprometido" value={usd(committed)} to="/admin/finance" />
          <Kpi label="Crédito disponible" value={usd(approved - committed)} to="/admin/finance" />
          <Kpi
            label="Saldos vencidos"
            value={data.overdue.available && data.overdue.real ? usd(data.overdue.real.overdueMicros) : "No disponible"}
            hint={
              data.overdue.available && data.overdue.test
                ? `${String(data.overdue.real?.overdueCount ?? 0)} facturas reales vencidas · prueba: ${usd(data.overdue.test.overdueMicros)}`
                : "Solo con permiso de finanzas"
            }
            to="/admin/billing"
          />
        </div>
      </Section>
      <Callout title="El crédito real no está habilitado">
        Las órdenes, reservas y consumos de staging son datos de prueba. El corte automático por presupuesto y la
        facturación son decisiones pendientes (ADR-0047, CA-O1 y CA-O4).
      </Callout>
    </div>
  );
}
